import { useEffect, useRef, useState } from "react";
import { Button } from "../../components/ui/button";

const AUTH_PATHS = new Set([
  "/login",
  "/register",
  "/forgot-password",
  "/reset-password",
  "/complete-registration",
]);

function getWelcomeStorageKey(userId) {
  return `ddp-v2-welcome-dismissed-${userId}`;
}

function getWelcomeSessionKey(userId) {
  return `ddp-v2-welcome-session-dismissed-${userId}`;
}

function shouldShowWelcome({ isGuest, pathname, userId }) {
  if (isGuest || !userId || AUTH_PATHS.has(pathname)) return false;

  try {
    const permanentlyDismissed = window.localStorage.getItem(getWelcomeStorageKey(userId)) === "true";
    const dismissedThisSession = window.sessionStorage.getItem(getWelcomeSessionKey(userId)) === "true";
    return !permanentlyDismissed && !dismissedThisSession;
  } catch {
    return true;
  }
}

export default function V2WelcomePopup({ isGuest, pathname, userId }) {
  const [isOpen, setIsOpen] = useState(() => shouldShowWelcome({ isGuest, pathname, userId }));
  const [doNotShowAgain, setDoNotShowAgain] = useState(false);
  const dismissedUserIdRef = useRef(null);

  useEffect(() => {
    if (dismissedUserIdRef.current !== userId) {
      dismissedUserIdRef.current = null;
    }

    if (dismissedUserIdRef.current === userId) {
      setIsOpen(false);
      return;
    }

    setIsOpen(shouldShowWelcome({ isGuest, pathname, userId }));
  }, [isGuest, pathname, userId]);

  if (!isOpen || isGuest || !userId || AUTH_PATHS.has(pathname)) return null;

  const dismiss = () => {
    dismissedUserIdRef.current = userId;

    try {
      window.sessionStorage.setItem(getWelcomeSessionKey(userId), "true");
    } catch {
      // Storage is optional; closing the popup must always remain available.
    }

    if (doNotShowAgain) {
      try {
        window.localStorage.setItem(getWelcomeStorageKey(userId), "true");
      } catch {
        // Storage is optional; closing the popup must always remain available.
      }
    }
    setIsOpen(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="v2-welcome-title"
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl border border-border bg-card p-6 text-card-foreground shadow-2xl"
      >
        <h2 id="v2-welcome-title" className="text-2xl font-extrabold">
          Welcome to Dialysis Diet Pro
        </h2>
        <div className="mt-4 space-y-3 text-sm leading-6 text-muted-foreground">
          <p>
            Dialysis Diet Pro helps you track daily food intake, monitor key nutrients, and stay organized with reminders.
          </p>
          <p>
            You can log foods, review your daily potassium, phosphorus, sodium, and protein totals, save custom foods,
            check your history, and set reminders by Push notification, WhatsApp, or both.
          </p>
          <p>
            For the best experience, complete your profile, enter your current weight, allow browser notifications, and
            add your phone number if you want WhatsApp reminders.
          </p>
          <p>
            This app is a support tool. It does not replace medical advice, dialysis treatment, or guidance from your
            doctor or renal dietitian. Always follow the limits and instructions given by your healthcare team.
          </p>
        </div>
        <label className="mt-5 flex items-center gap-2 text-sm font-medium">
          <input
            type="checkbox"
            checked={doNotShowAgain}
            onChange={(event) => setDoNotShowAgain(event.target.checked)}
          />
          Don’t show this again
        </label>
        <Button className="mt-5 w-full sm:w-auto" onClick={dismiss}>
          I understand
        </Button>
      </section>
    </div>
  );
}
